import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, PlayCircle, Paperclip, Search, Sparkles, WandSparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DOMAINES, domaineLabel } from "@/lib/api";
import { openDocument } from "@/pages/Documents";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import SourcesNotices from "@/components/SourcesNotices";

const imageSrc = (url) => (!url ? null : url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

const TutoCard = ({ t, i, prefix = "tuto" }) => (
  <motion.article
    data-testid={`${prefix}-card-${t.id}`}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.04 }}
    className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-400 transition-colors"
  >
    <div className="flex items-center justify-between gap-3">
      <div className="bk-label">{domaineLabel(t.domaine)}</div>
      {typeof t.pertinence === "number" && (
        <span className="text-[10px] uppercase tracking-wider rounded-full bg-[#005CFF]/10 text-[#005CFF] px-2.5 py-1">{t.pertinence}% pertinent</span>
      )}
    </div>
    <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 mt-2">{t.titre}</h2>
    {t.raison && <p className="text-sm text-[#005CFF] mt-1.5">{t.raison}</p>}
    <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap leading-relaxed">{t.texte}</p>
    {t.images?.length > 0 && (
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`tuto-images-${t.id}`}>
        {t.images.map((url) => (
          <a
            key={url}
            href={imageSrc(url)}
            target="_blank"
            rel="noreferrer"
            data-testid={`tuto-image-open-${url.split("/").pop()}`}
            className="block overflow-hidden rounded-xl border border-slate-200 hover:border-[#005CFF] transition-colors"
          >
            <img src={imageSrc(url)} alt={t.titre} loading="lazy" className="h-28 w-full object-cover hover:scale-[1.03] transition-transform duration-300" />
          </a>
        ))}
      </div>
    )}
    {t.video_url && (
      <a href={t.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[#005CFF] hover:underline">
        <PlayCircle size={16} /> Voir la vidéo
      </a>
    )}
    {t.medias?.length > 0 && (
      <div className="mt-4 space-y-2" data-testid={`tuto-medias-${t.id}`}>
        <div className="bk-label">Documents joints</div>
        {t.medias.map((m) => (
          <button
            key={m.id}
            data-testid={`tuto-media-open-${m.id}`}
            onClick={() => openDocument(`/documents/${m.id}/download`, m.filename)}
            className="w-full flex items-center gap-2 text-left text-sm text-[#005CFF] hover:underline"
          >
            <Paperclip size={14} /> {m.titre || m.filename} <span className="text-xs text-slate-400">({Math.round((m.size || 0) / 1024)} Ko)</span>
          </button>
        ))}
      </div>
    )}
    {t.tags?.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-4">
        {t.tags.map((tag) => (
          <span key={tag} className="text-xs rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">#{tag}</span>
        ))}
      </div>
    )}
  </motion.article>
);

const EXEMPLES = ["Mon Loxone ne répond plus", "Une caméra est floue la nuit", "Le disjoncteur saute quand j'allume le four"];

const MARQUES = [
  { key: "daikin", label: "Daikin", alias: ["daikin", "onecta", "madoka"] },
  { key: "loxone", label: "Loxone", alias: ["loxone", "miniserver"] },
  { key: "unifi", label: "UniFi", alias: ["unifi", "ubiquiti", "udm"] },
  { key: "reyee", label: "Reyee", alias: ["reyee", "ruijie"] },
  { key: "unv", label: "UNV", alias: ["unv", "uniview", "ezview", "ezstation"] },
  { key: "heiwa", label: "Heiwa", alias: ["heiwa"] },
  { key: "schneider", label: "Schneider", alias: ["schneider", "wiser", "acti9", "resi9", "odace", "unica"] },
  { key: "legrand", label: "Legrand", alias: ["legrand", "celiane", "céliane", "netatmo", "dx3", "mosaic", "dooxie"] },
];

const correspondMarque = (tuto, marque) => {
  const foin = [tuto.titre, tuto.texte, ...(tuto.tags || []), ...(tuto.materiel || [])].join(" ").toLowerCase();
  return marque.alias.some((a) => foin.includes(a));
};

export default function Tutos() {
  const { user } = useAuth();
  const [tutos, setTutos] = useState([]);
  const [domaine, setDomaine] = useState(null);
  const [marque, setMarque] = useState(null);
  const [q, setQ] = useState("");
  const [monMateriel, setMonMateriel] = useState(false);
  const [panne, setPanne] = useState("");
  const [ia, setIa] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);

  const materiels = useMemo(() => (user?.contact?.materiel || []).map((m) => m.designation), [user]);

  useEffect(() => {
    const params = {};
    if (domaine) params.domaine = domaine;
    if (q) params.q = q;
    setTutos([]);
    api.get("/tutorials", { params }).then(({ data }) => setTutos(data));
  }, [domaine, q]);

  const chercherIa = async (texte) => {
    const message = (texte ?? panne).trim();
    if (message.length < 5) return toast.error("Décrivez votre problème en quelques mots");
    setPanne(message);
    setIaLoading(true);
    setIa(null);
    try {
      const { data } = await api.post("/tutorials/search-ia", { message });
      setIa(data);
    } catch (e) {
      toast.error(errMsg(e, "Recherche IA indisponible"));
    } finally {
      setIaLoading(false);
    }
  };

  // Rapprochement souple : « Daikin VRV IV » chez le client correspond au tuto « Daikin split ».
  const concerne = (tuto) =>
    !tuto.materiel?.length ||
    tuto.materiel.some((m) => {
      const marque = m.toLowerCase().split(" ")[0];
      return materiels.some((d) => {
        const client = d.toLowerCase();
        return client.includes(m.toLowerCase()) || m.toLowerCase().includes(client) || client.includes(marque);
      });
    });

  const visibles = (monMateriel && materiels.length ? tutos.filter(concerne) : tutos).filter(
    (t) => !marque || correspondMarque(t, marque)
  );

  return (
    <div className="space-y-7" data-testid="tutos-page">
      <div>
        <div className="bk-label mb-2">Aide en autonomie</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Librairie</h1>
        <p className="text-sm text-slate-600 mt-2">Tutoriels, notices et documents techniques de votre installation, marque par marque.</p>
      </div>

      <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-5 sm:p-6 space-y-4" data-testid="tutos-ia-panel">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50">
            <WandSparkles size={14} className="text-[#005CFF]" /> Recherche de documents
          </div>
          <h2 className="font-display text-lg md:text-lg font-bold tracking-tight mt-2">Dites ce que vous cherchez, avec vos mots</h2>
          <p className="text-sm text-white/60 mt-1">L'assistant retrouve le tutoriel, la notice ou le document technique correspondant à votre installation.</p>
        </div>
        <Textarea
          data-testid="tutos-ia-input"
          value={panne}
          onChange={(e) => setPanne(e.target.value)}
          rows={3}
          placeholder="Ex : depuis l'orage, mon écran Loxone reste noir et le portail ne s'ouvre plus"
          className="bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-[#005CFF]"
        />
        <div className="flex flex-wrap gap-2">
          {EXEMPLES.map((ex, i) => (
            <button
              key={ex}
              data-testid={`tutos-ia-exemple-${i}`}
              onClick={() => chercherIa(ex)}
              className="text-xs rounded-full border border-white/15 px-3 py-1.5 text-white/70 hover:border-[#005CFF] hover:text-white transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
        <Button data-testid="tutos-ia-btn" onClick={() => chercherIa()} disabled={iaLoading} className="bg-[#005CFF] hover:bg-[#0047CC]">
          {iaLoading ? <Loader2 size={15} className="animate-spin mr-2" /> : <Sparkles size={15} className="mr-2" />}
          {iaLoading ? "Recherche en cours…" : "Trouver le document"}
        </Button>

        {ia && (
          <div className="space-y-3 pt-2 border-t border-white/10" data-testid="tutos-ia-result">
            {ia.reponse && <p className="text-sm text-white/85 leading-relaxed" data-testid="tutos-ia-reponse">{ia.reponse}</p>}
            {ia.domaine_probable && (
              <div className="text-xs text-white/50">
                Domaine identifié : <span className="text-white font-semibold">{domaineLabel(ia.domaine_probable)}</span>
              </div>
            )}
            {ia.tutos.length === 0 && <p className="text-sm text-white/60">Aucun tutoriel ne correspond précisément à cette situation.</p>}
            <SourcesNotices sources={ia.sources} sombre />
            {ia.suggere_ticket && (
              <Link
                to={ia.domaine_probable ? `/declarer/${ia.domaine_probable}` : "/"}
                data-testid="tutos-ia-ticket-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#4D93FF] hover:text-white transition-colors"
              >
                Déclarer une panne au SAV <ArrowRight size={15} />
              </Link>
            )}
          </div>
        )}
      </div>

      {ia?.tutos?.length > 0 && (
        <div className="space-y-4" data-testid="tutos-ia-suggestions">
          <div className="bk-label">Documents suggérés ({ia.tutos.length})</div>
          {ia.tutos.map((t, i) => (
            <TutoCard key={`ia-${t.id}`} t={t} i={i} prefix="tuto-ia" />
          ))}
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="tutos-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher : filtres, NVR, disjoncteur…" className="h-12 pl-11" />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          data-testid="tutos-filter-all"
          onClick={() => setDomaine(null)}
          className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${!domaine ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
        >
          Tous
        </button>
        {DOMAINES.map((d) => (
          <button
            key={d.key}
            data-testid={`tutos-filter-${d.key}`}
            onClick={() => setDomaine(d.key)}
            className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${domaine === d.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="tutos-marques">
        <button
          data-testid="tutos-marque-all"
          onClick={() => setMarque(null)}
          className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${!marque ? "border-[#005CFF] text-[#005CFF] bg-blue-50" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
        >
          Toutes marques
        </button>
        {MARQUES.map((m) => (
          <button
            key={m.key}
            data-testid={`tutos-marque-${m.key}`}
            onClick={() => setMarque(marque?.key === m.key ? null : m)}
            className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${marque?.key === m.key ? "border-[#005CFF] text-[#005CFF] bg-blue-50" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {materiels.length > 0 && (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="tutos-materiel-toggle">
            <input type="checkbox" checked={monMateriel} onChange={(e) => setMonMateriel(e.target.checked)} className="accent-[#005CFF]" />
            <Sparkles size={14} className="text-[#005CFF]" /> N'afficher que les tutos liés à mon installation
          </label>
          {monMateriel && tutos.length > visibles.length && (
            <button
              data-testid="tutos-afficher-tout"
              onClick={() => setMonMateriel(false)}
              className="text-xs font-semibold text-[#005CFF] hover:underline"
            >
              {tutos.length - visibles.length} tutoriel(s) masqué(s) — tout afficher
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {visibles.map((t, i) => (
          <TutoCard key={t.id} t={t} i={i} />
        ))}
        {visibles.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="tutos-empty">
            Aucun tutoriel {marque ? `${marque.label} ` : ""}pour cette sélection.{" "}
            {marque && (
              <button data-testid="tutos-reset-marque" onClick={() => setMarque(null)} className="font-semibold text-[#005CFF] hover:underline">
                Voir toutes les marques
              </button>
            )}
            {!marque && tutos.length > 0 && (
              <button data-testid="tutos-voir-tous" onClick={() => setMonMateriel(false)} className="font-semibold text-[#005CFF] hover:underline">
                Voir les {tutos.length} tutoriels
              </button>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
